/**
 * filter-form.js
 */

import React from 'react';
import { Grid, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm, useWatch, FormProvider } from 'react-hook-form';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatISO, parseISO, startOfDay, endOfDay } from 'date-fns';
import { Types } from 'utils/constants';
import { getDatesFromRangeType } from 'utils/datetime';
import logger from 'utils/logger';
import { makeLocation } from 'utils/routing';
import { getMultiSelectHandlerOptions } from 'utils/misc';
import {
  getFilterParamsObj,
  getFilterParamsString
} from 'utils/filter-params';
import { UserContext } from 'contexts/user/user-context';
import useRecordStore from 'contexts/record-store';
import FeatureBox from 'components/feature-box';
import Select from 'components/form-inputs/select';
import HiddenInput from 'components/form-inputs/hidden-input';
import MultiSelect from 'components/multi-select';
import FilterCriteria from './filter-criteria';
import { useFilterCriteriaSpecs } from './filter-criteria-specs';


const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 'calc(90% + 32px)',
    '& .MuiToggleButtonGroup-vertical': {
      width: '85%',
      height: 'fit-content',
    },
  },
  formGrid: {
    '&.MuiGrid-container': {
      marginTop: 0,
    },
    '& > .MuiGrid-item': {
      padding: `0 ${theme.spacing(2)}`,
      '& .MuiFormControl-root': {
        margin: `${theme.spacing(2.5)} 0`,
        width: '100%',
      },
    },
  },
  criteriaRow: {
    alignItems: 'center',
  },
  criteriaForm: {
    alignItems: 'center',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey.colors[9],
  },
  detailedDateFields: {
    backgroundColor: theme.palette.sys.surface,
    borderRadius: theme.spacing(1),    
  },
}));


const LocalTypes = {
  // Date Range Picker Types
  DateRangePicker: {
    START: 0,
    END: 1,
  },
};


// Each row in the form is a filter 'criteria' consisting of 2 or 3 fields with
// varying component types based on the selected criteria type. The type and filterType
// are both required as well as one value for field 2.
const criteriaFormDefaults = {
  type: ' ',    // Field 1: Criteria name. ex: 'complete', 'odorType', 'exerciseName'
  ddVal: 0,     // Field 2 (optional): Dropdown value. ex: <numeric> - id of (Yes/No, Locker/Room/Vehicle)
  textVal: '',  // Field 2 (optional): Text value. ex: 'exercise drills', 'climbing'
  tagVals: [],  // Field 2 (optional): Tag values. ex: Demo, Scent Wall, Certification
  ddVal2: 0,    // Field 3 (optional): Dropdown value (2 of 2). ex: <numeric> - id of 'Cocaine', 'Ecstasy', 'Heroin'
  type2: '',    // Hidden field (optional): Criteria name for second value. ex: 'OdorSubType'
  fieldType: 0, // Hidden field: Criteria field type. ex: <numeric> - text, dropdown, dropdown2
};

const { startDate, endDate } = getDatesFromRangeType(Types.DateRange.ALL_TIME);
const formDefaults = {
  recordGroup: Types.RecordGroup.ALL,
  workMode: Types.WorkMode.ALL,
  patrolType: '',
  dateRange: Types.DateRange.ALL_TIME,
  dogId: 0,
  complete: '',
  reviewStatus: '',
  outdated: '',
  criteriaList: [],
  startDate,
  endDate
};


export default function FilterForm(props) {
  const {
    options,
    setIsFilterFormValid,
    onSubmit,
  } = props;
  const classes = useStyles(props);
  const { user } = React.useContext(UserContext);
  const { t } = useTranslation('general');

  const history = useHistory();
  const { params } = useRouteMatch();
  const filterParamsString = params.filterParamsString;

  const recordStore = useRecordStore();
  const status = recordStore.useStatus();
  const isLoading = status.isLoading.get();

  const handlerDogs = options?.handlerDogs;
  const criteriaSpecs = useFilterCriteriaSpecs(t, options);

  const [dateRange, setDateRange] = React.useState({
    startDate: formDefaults.startDate,
    endDate: formDefaults.endDate
  });
  const [pickerStartDate, setPickerStartDate] = React.useState(formDefaults.startDate);
  const [pickerEndDate, setPickerEndDate] = React.useState(formDefaults.endDate);
  const [pickerStartDateError, setPickerStartDateError] = React.useState();
  const [pickerEndDateError, setPickerEndDateError] = React.useState();

  const formContext = useForm({ default: formDefaults });
  const { control, handleSubmit, reset, getValues, setValue } = formContext;
  const formValues = useWatch({ control });

  
  // generate handler dropdown options based on role (super or trainer)
  const managedHandlers = options?.managedHandlers;
  const supervisors = options?.supervisors;
  const handlerOptions = React.useMemo(() =>
    getMultiSelectHandlerOptions(user, supervisors, managedHandlers),
    [user, supervisors, managedHandlers]);

    
  const dogOptions = React.useMemo(() => {
    return user.role.isHandler
      ? handlerDogs?.[0]?.dogs
      : formValues.handlers?.length === 1
        ? handlerDogs?.find(h => h.id === parseInt(formValues.handlers[0]))?.dogs
        : null;
  }, [user.role.isHandler, handlerDogs, formValues.handlers]);


  // Merge the received URL params into the standard form defaults object
  const updateDefaultsWithParams = React.useCallback((formDefaults, paramsString) => {
    const params = getFilterParamsObj(paramsString) || {};

    // Clone current defaults before applying updates
    let newDefaults = { ...formDefaults, criteriaList: [] };
    let critId = 0;

    // Extend defaults based on received URL params
    Object.entries(params).forEach(([critType, critVal]) => {
      // back-end supports multiple record groups (array value) but filter form doesn't.
      // check if critType is recordGroups and if so convert name and use first array element
      // as single record group value.
      if (critType === 'recordGroups') {
        critType = 'recordGroup';
        critVal = critVal[0];
      }

      const matchedCrit = criteriaSpecs.find(obj => obj.type === critType);

      // If param type matches a type in the crit spec then add it to the criteriaList
      if (matchedCrit) {
        if (!matchedCrit.isSecondaryField) {
          const crit = { ...criteriaFormDefaults };
          crit.type = critType;
          crit.fieldType = matchedCrit.fieldType;

          switch (crit.fieldType) {
            case Types.FilterCriteriaField.DROPDOWN:
              crit.ddVal = parseInt(critVal);

              // Get matching sub criteria (if any) for the second dropdown
              if (matchedCrit.linkedType) {
                crit.type2 = matchedCrit.linkedType;
                const param = Object.entries(params).find(([key,]) =>
                  key === matchedCrit.linkedType);
                crit.ddVal2 = param ? parseInt(param[1]) : criteriaFormDefaults.ddVal2;
              }
              break;
            case Types.FilterCriteriaField.TEXT:
              crit.textVal = critVal;
              break;
            case Types.FilterCriteriaField.TAG:
              // Special case for any tag
              crit.tagVals = critVal.map(v => v === 'any' ? 'any' : Number(v));
              break;
            default:
          }

          newDefaults.criteriaList[critId] = crit;
          critId++;
        }
      }
      else if (critType === 'startDate' || critType === 'endDate') {
        newDefaults[critType] = parseISO(critVal);
      } 
      else if (critType === 'date') {
        newDefaults.startDate = parseISO(critVal);
        newDefaults.endDate = parseISO(critVal);
        newDefaults.dateRange = Types.DateRange.CUSTOM;
      }
      else if (Array.isArray(critVal)) {
        // Special case for any tag
        newDefaults[critType] = critVal.map(v => v === 'any' ? v : parseInt(v));
      }
      else {
        newDefaults[critType] = parseInt(critVal);
      }
    });

    return newDefaults;
  }, [criteriaSpecs]);


  // Initialize form default values using combination of hardcoded defaults,
  // URL params and active recordGroup filter
  React.useEffect(() => {
    const defaultValues = updateDefaultsWithParams(formDefaults, filterParamsString);
    let { startDate, endDate } = defaultValues;

    // Disable some filter fields for training classes
    if (defaultValues.recordGroup === Types.RecordGroup.CLASS) {
      defaultValues.workMode = null;
      defaultValues.patrolType = null;
      defaultValues.dogId = null;
    }

    // Clear any patrol type selections if detection workmode is active
    if (defaultValues.workMode === Types.WorkMode.DETECTION) {
      defaultValues.patrolType = null;
    }

    // Make sure that Complete is selected if any Review value is set
    if ([0, 1, 2].includes(defaultValues.reviewStatus)) {
      defaultValues.complete = 1;
    }

    // Set initial form values
    reset(defaultValues);


    if (
      defaultValues.dateRange === Types.DateRange.CUSTOM
      && (!startDate || !endDate)
    ) {
      const { startDate: monthBeginDate, endDate: monthEndDate } = getDatesFromRangeType(Types.DateRange.THIS_MONTH);
      startDate = monthBeginDate;
      endDate = monthEndDate;
    }

    setDateRange({ startDate, endDate });
    setPickerStartDate(startDate);
    setPickerEndDate(endDate);

  }, [filterParamsString, updateDefaultsWithParams, reset]);


  // Reset dog field if the selected dog isn't among the options
  // (e.g. if the selected handler changes)
  React.useEffect(() => {
    if (formValues.dogId && !dogOptions?.find(d => d.id === formValues.dogId)) {
      setValue('dogId', 0);
    }
  }, [dogOptions, formValues.dogId, setValue]);


  const getCriteriaFromForm = (data) => {
    if (!data.criteriaList) {
      return null;
    }

    let criteriaList = data.criteriaList.map(criteria => {
      const fieldType = parseInt(criteria.fieldType);
      if (criteria.type.length > 1) {
        if (fieldType === Types.FilterCriteriaField.DROPDOWN) {
          return criteria.ddVal2 ?
            { [criteria.type]: criteria.ddVal, [criteria.type2]: criteria.ddVal2 } :
            { [criteria.type]: criteria.ddVal };
        }
        else if (fieldType === Types.FilterCriteriaField.TEXT) {
          return criteria.textVal ? { [criteria.type]: criteria.textVal } : null;
        }
        else if (fieldType === Types.FilterCriteriaField.TAG) {
          return criteria.tagVals.length > 0 ? { [criteria.type]: criteria.tagVals } : null;
        }
      }
      return null;
    });

    return Object.assign({}, ...criteriaList);
  }


  const getFilterParamsFromForm = React.useCallback((data) => {
    const { startDate, endDate } = dateRange;
    // const startDate = pickerStartDate;
    // const endDate = pickerEndDate;
    const criteriaList = getCriteriaFromForm(data);

    const filterParams = {
      // convert single record group selection to array as back-end expects/supports this
      ...(data.recordGroup && { recordGroups: [data.recordGroup] }),
      ...(data.workMode && { workMode: data.workMode }),
      ...(data.dateRange && { dateRange: data.dateRange }),
      ...((Boolean(data.patrolType) || data.patrolType === 0) && { patrolType: data.patrolType }),
      ...(startDate && { startDate: formatISO(startDate, { representation: 'date' }) }),
      ...(endDate && { endDate: formatISO(endDate, { representation: 'date' }) }),
      ...(data.handlers && { handlers: data.handlers }),
      ...(data.dogId && { dogId: data.dogId }),
      ...((data.complete === 1 || data.complete === 0) && { complete: data.complete }),
      ...((data.reviewStatus === 2 || data.reviewStatus === 1 || data.reviewStatus === 0) && { reviewStatus: data.reviewStatus }),
      ...((data.outdated === 1 || data.outdated === 0) && { outdated: data.outdated }),
      ...criteriaList,
    };

    return filterParams;
    // }, [pickerStartDate, pickerEndDate]);
  }, [dateRange]);

  const isDateModeDetailed = getValues().dateRange !== Types.DateRange.ALL_TIME


  const handleFilterSubmit = (formData) => {
    logger.log('[filter-form.js] Submitting filter form.', formData);

    const filterParams = getFilterParamsFromForm(formData);

    const filterParamsString = getFilterParamsString(filterParams);
    const to = makeLocation('', 'records', 'params', {
      p1: Types.FilterMode.FILTER,
      filterParams: filterParamsString
    });

    history.push(to);

    // onSubmit prop
    onSubmit();
  };


  const handleDateRangeChange = (dateRangeType) => {
    if (dateRangeType === Types.DateRange.CUSTOM) {
      if (!dateRange.startDate || !dateRange.endDate) {
        const { startDate, endDate } = getDatesFromRangeType(Types.DateRange.THIS_MONTH);
        setDateRange({ startDate, endDate });
        setPickerStartDate(startDate);
        setPickerEndDate(endDate);
      }
    } else {
      const { startDate, endDate } = getDatesFromRangeType(dateRangeType);
      setDateRange({ startDate, endDate });
      setPickerStartDate(startDate);
      setPickerEndDate(endDate);
    }
  }


  const handleDatePickerChange = (date, forDateRangePickerType) => {
    var procDate;

    switch (forDateRangePickerType) {
      case LocalTypes.DateRangePicker.START:
        procDate = startOfDay(date);
        setPickerStartDate(procDate);
        setDateRange({ startDate: procDate, endDate: pickerEndDate });
        break;
      case LocalTypes.DateRangePicker.END:
        procDate = endOfDay(date);
        setPickerEndDate(procDate);
        setDateRange({ startDate: pickerStartDate, endDate: procDate });
        break;
      default:
    }

    setValue('dateRange', Types.DateRange.CUSTOM);
  };


  // Disable filtering if either custom date is invalid
  React.useEffect(() => {
    if (pickerStartDateError || pickerEndDateError) {
      setIsFilterFormValid(false);
    } else {
      setIsFilterFormValid(true);
    }
  }, [setIsFilterFormValid, pickerStartDateError, pickerEndDateError])

  
  const handlePatrolTypeSelection = (selected) => {
    if (selected !== '') {
      setValue('workMode', Types.WorkMode.PATROL);

      const trainingTypes = Types.Patrol.Training.values();
      const deployTypes = Types.Patrol.Deployment.values();
      const trainingOnlyTypes = Types.Patrol.Training.values(deployTypes);
      const deployOnlyTypes = Types.Patrol.Deployment.values(trainingTypes);

      if (selected === Types.Patrol.SCENARIO || trainingOnlyTypes.includes(selected)) {
        setValue('recordGroup', Types.RecordGroup.TRAINING);
      } else if (deployOnlyTypes.includes(selected)) {
        setValue('recordGroup', Types.RecordGroup.DEPLOYMENT);
      }
    }
  }


  // handle selected handler changes
  const handleSelectHandlers = (handlerIds) => {
    setValue('handlers', handlerIds.length > 0 ? handlerIds : '');
  };


  const getPatrolOptions = () => {
    const commonEntries = [{ id: '', name: 'All Patrol Types' }];
    let recordTypeEntries = [];

    if (formValues.recordGroup === Types.RecordGroup.ALL || formValues.recordGroup === Types.RecordGroup.CLASS) {
      recordTypeEntries = Types.Patrol.entries(t, user.role.isTrainer ? [Types.Patrol.NONSEARCH] : []);
    }
    else if (formValues.recordGroup === Types.RecordGroup.TRAINING) {
      const scenarioName = Types.Patrol.getName(t, Types.Patrol.SCENARIO);
      recordTypeEntries = [
        { id: Types.Patrol.SCENARIO, name: scenarioName },
        ...Types.Patrol.Training.entries(t)];
    }
    else if (formValues.recordGroup === Types.RecordGroup.DEPLOYMENT) {
      recordTypeEntries = Types.Patrol.Deployment.entries(t);
    }

    return [
      ...commonEntries,
      ...recordTypeEntries
    ];
  }


  if (!options || Object.keys(formValues).length === 0) return null;


  return (
    <>
      <FeatureBox style={{ flex: 1 }} loading={isLoading}>
        <FormProvider {...formContext}>
          <form
            id="filter-form"
            onSubmit={handleSubmit(handleFilterSubmit)}
            className={classes.form}
          >
            <Grid container className={classes.formGrid}>

              {/* record group options */}
              <Grid item xs={12} sm={6} md={4}>
                <Select
                  name="recordGroup"
                  label="Record Type"
                  onChange={(selected) => {
                    setValue('workMode', selected === Types.RecordGroup.CLASS ? null : 0);
                    setValue('patrolType', selected === Types.RecordGroup.CLASS ||
                      formValues.workMode === Types.WorkMode.DETECTION ? null : '');
                    setValue('dogId', selected === Types.RecordGroup.CLASS ? null : 0);
                  }}
                  options={Types.RecordGroup
                    .entries(t, user.role.isTrainer ? [Types.RecordGroup.DEPLOYMENT] : [])
                    .map(({ id, name }) => ({
                      id,
                      name,
                    }))}
                />
              </Grid>

              {/* workmode options */}
              <Grid item xs={12} sm={6} md={4}>
                <Tooltip
                  arrow
                  placement="top"
                  title={formValues.recordGroup === Types.RecordGroup.CLASS
                    ? `Select Exercises ${!user.role.isTrainer ? 'or Deployment ' : ''}to enable this section`
                    : ''}
                >
                  <span>
                    <Select
                      name="workMode"
                      label="Work Mode"
                      disabled={formValues.recordGroup === Types.RecordGroup.CLASS}
                      onChange={(selected) => {
                        setValue('patrolType', selected === Types.WorkMode.DETECTION ||
                          formValues.recordGroup === Types.RecordGroup.CLASS ? null : '');
                      }}
                      options={Types.WorkMode.entries(t).map(({ id, name }) => ({
                        id,
                        name: name === 'All' ? 'Detect & Patrol' : name,
                      }))}
                    />
                  </span>
                </Tooltip>
              </Grid>

              {/* patrol type */}
              <Grid item xs={12} sm={6} md={4}>
                <Tooltip
                  arrow
                  placement="top"
                  title={formValues.workMode === Types.WorkMode.DETECTION
                    ? 'Select the Patrol option to enable this section'
                    : formValues.recordGroup === Types.RecordGroup.CLASS
                      ? `Select Exercises ${!user.role.isTrainer ? 'or Deployment ' : ''}to enable this section`
                      : ''}
                >
                  <span>
                    <Select
                      name="patrolType"
                      label="Patrol Type"
                      onChange={(selected) => handlePatrolTypeSelection(selected)}
                      disabled={formValues.workMode === Types.WorkMode.DETECTION
                        || formValues.recordGroup === Types.RecordGroup.CLASS
                      }
                      displayEmpty
                      options={getPatrolOptions().map(({ id, name }) => ({
                        id,
                        name,
                      }))}
                    />
                  </span>
                </Tooltip>
              </Grid>

              {/* handler options */}
              {!user.role.isHandler &&
                <Grid item xs={12} sm={6} md={4}>
                  <HiddenInput name='handlers' />
                  <MultiSelect
                    id='filter-handler-select'
                    label='Handlers'
                    nounPlural='Handlers'
                    options={handlerOptions}
                    value={formValues.handlers || []}
                    onChange={handleSelectHandlers}
                  />                  
                </Grid>
              }

              {/* dog options */}
              {(dogOptions || !user.role.isHandler) &&
                <Grid item xs={12} sm={6} md={4}>
                  <Tooltip
                    arrow
                    placement="top"
                    title={formValues.recordGroup === Types.RecordGroup.CLASS
                      ? `Select Exercises ${!user.role.isTrainer ? 'or Deployment ' : ''}to enable this section`
                      : (!user.role.isHandler && formValues.handlers?.length !== 1)
                        ? 'Select a single handler to enable this section'
                        : ''
                    }
                  >
                    <span>
                      <Select
                        name="dogId"
                        label="Dog"
                        disabled={formValues.recordGroup === Types.RecordGroup.CLASS || !dogOptions}
                        options={[
                          { id: 0, name: 'All Dogs' },
                          ...(dogOptions || [])
                        ].map(({ id, name }) => ({
                          id,
                          name,
                        }))}
                      />
                    </span>
                  </Tooltip>
                </Grid>
              }
              
              {/* date range options */}
              <Grid item xs={12} sm={6} md={4} className={isDateModeDetailed ? classes.detailedDateFields : undefined}>
                <Select
                  name="dateRange"
                  label="Date Range"
                  onChange={(selected) => handleDateRangeChange(selected.target.value)}
                  options={Types.DateRange.entries(t).map(({ id, name }) => ({
                    id,
                    name,
                  }))}
                />
                {isDateModeDetailed && (
                  <>
                    <DatePicker
                      closeOnSelect
                      slotProps={{
                        textField: { variant: 'outlined', size: 'small' },
                      }}
                      label="Start"
                      format="MM/dd/yyyy"
                      value={pickerStartDate}
                      onChange={(date) => handleDatePickerChange(date, LocalTypes.DateRangePicker.START)}
                      onError={setPickerStartDateError}
                      onAccept={() => setPickerStartDateError(null)}
                    />
                    <DatePicker
                      closeOnSelect
                      slotProps={{
                        textField: { variant: 'outlined', size: 'small' }
                      }}
                      label="End"
                      format="MM/dd/yyyy"
                      value={pickerEndDate}
                      minDate={pickerStartDate}
                      minDateMessage="Date should not be before Start date"
                      onChange={(date) => handleDatePickerChange(date, LocalTypes.DateRangePicker.END)}
                      onError={setPickerEndDateError}
                      onAccept={() => setPickerEndDateError(null)}
                    />
                  </>
                )}
              </Grid>
            </Grid>

            {/* Filter criteria forms */}
            <FilterCriteria emptyCriteria={criteriaFormDefaults} />
          </form>
        </FormProvider>
      </FeatureBox>
    </>
  );
}
